import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_AUTH_BACKEND_URL || '';

/**
 * Load the workflow builder's node model slots.
 *
 * GET /workflows/model-slots serves the backend's slot table
 * (MODEL_SLOT_RULES) with each slot's default resolved from the workflow's
 * profile, exactly as a run resolves it, so the pickers can't drift from
 * the backend.  Reloads when the pinned or the active profile changes,
 * since either can change a slot's default.
 *
 * @param {string|null} workflowProfile - The workflow's pinned profile, or
 *   null to follow the active profile.
 * @param {string} [activeProfileName] - The active profile's name.
 * @returns {{modelSlots: Array|null, error: string|null}} `modelSlots` is
 *   null until the first load succeeds; a failed reload keeps the last
 *   table and sets `error`.
 */
export function useModelSlots(workflowProfile, activeProfileName) {
  const [modelSlots, setModelSlots] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const query = workflowProfile
      ? `?profile=${encodeURIComponent(workflowProfile)}`
      : '';
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/workflows/model-slots${query}`, {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status !== 'ok') {
          throw new Error(
            typeof data.detail === 'string'
              ? data.detail
              : `HTTP ${res.status}`,
          );
        }
        if (cancelled) return;
        setModelSlots(Array.isArray(data.model_slots) ? data.model_slots : []);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load model options');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [workflowProfile, activeProfileName]);

  return { modelSlots, error };
}
